import { computed } from 'vue';
import { useTranslation } from './useTranslation';
import { useLocaleStore } from '@/stores/localeStore';

export type EanRegistryFieldType = {
    key: string;
    field: string;
    label: string;
    required: boolean;
};

// Import mapping suffix for a column whose language the backend detects per cell.
export const AUTO_LANGUAGE_KEY = 'auto';

/**
 * Composable for generating EAN registry available fields
 * Used in EAN import dialog and price templates
 *
 * `includeAutoLanguage` adds a "<Field> (Auto-detect)" option per translatable
 * field — only the import understands it.
 */
export function useEanRegistryFields(options: { includeAutoLanguage?: boolean } = {}) {
    const { t, getCurrencies } = useTranslation();
    const localeStore = useLocaleStore();

    const languages = computed(() =>
        localeStore.languages.map(lang => ({
            value: lang.code,
            name: lang.name,
        })),
    );

    const translatableFields = (field: string, labelKey: string, requiredFirst = false): EanRegistryFieldType[] => {
        const fields = languages.value.map((lang, index) => ({
            key: `${field}.${lang.value}`,
            field: `${field}.${lang.value}`,
            label: `${t(labelKey)} (${lang.value.toUpperCase()})`,
            required: requiredFirst && index === 0,
        }));

        if (options.includeAutoLanguage) {
            fields.unshift({
                key: `${field}.${AUTO_LANGUAGE_KEY}`,
                field: `${field}.${AUTO_LANGUAGE_KEY}`,
                label: `${t(labelKey)} (${t('eanRegistry.import.autoLanguage')})`,
                required: false,
            });
        }

        return fields;
    };

    const availableFields = computed<EanRegistryFieldType[]>(() => {
        const currencies = getCurrencies();

        const nameFields = translatableFields('name', 'eanRegistry.name', true);
        const descriptionFields = translatableFields('description', 'eanRegistry.description');
        const genderFields = translatableFields('gender', 'eanRegistry.gender');
        const compositionFields = translatableFields('composition', 'eanRegistry.composition');
        const categoryFields = translatableFields('category', 'eanRegistry.category');

        const priceFields = currencies.map(currency => ({
            key: `prices.${currency.value}`,
            field: `prices.${currency.value}`,
            label: `${t('eanRegistry.price')} (${currency.value})`,
            required: false,
        }));

        return [
            { key: 'ean', field: 'ean', label: t('eanRegistry.ean'), required: true },
            ...nameFields,
            ...descriptionFields,
            ...genderFields,
            ...compositionFields,
            ...categoryFields,
            { key: 'brand', field: 'brand', label: t('eanRegistry.brand'), required: false },
            ...priceFields,
            { key: 'vat_rate', field: 'vat_rate', label: t('eanRegistry.vatRate'), required: false },
            { key: 'unit', field: 'unit', label: t('eanRegistry.unit'), required: false },
            { key: 'status', field: 'status', label: t('common.status'), required: false },
        ];
    });

    return {
        availableFields,
        languages,
    };
}
